import { useEffect, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { _t } from '@/i18n/t';
import { foldProgressLines } from '../utils/scanProgress';
import type { AudienceFact } from '../hooks/scanEventRouter';
import { useStaggeredReveal } from '../hooks/useStaggeredReveal';

const AUDIENCE_FACT_GAP_MS = 8000;
const AUDIENCE_FACT_MAX_VALUES = 3;

function factValue(fact: AudienceFact): string {
  const text = fact.values.slice(0, AUDIENCE_FACT_MAX_VALUES).join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function AudienceFacts({ facts }: { facts: AudienceFact[] }) {
  const shown = useStaggeredReveal(facts.length, AUDIENCE_FACT_GAP_MS);
  if (shown === 0) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="onboarding-scan-audience-facts">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {_t('onboarding.crawl.chapter.customers.factsHeading')}
      </div>
      {facts.slice(0, shown).map(fact => (
        <div key={fact.field} className="flex flex-col gap-0.5" data-testid="onboarding-scan-audience-fact">
          <span className="text-sm text-muted-foreground">
            {_t(`onboarding.crawl.chapter.customers.fact.${fact.field}`)}
          </span>
          <span className="text-foreground">
            {factValue(fact)}
            {fact.inferred && (
              <span className="ml-2 text-sm text-muted-foreground">
                {_t('onboarding.crawl.chapter.customers.fact.inferred')}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

const WAITING_MESSAGES = [
  _t('onboarding.crawl.chapter.customers.detail0'),
  _t('onboarding.crawl.chapter.customers.detail1'),
  _t('onboarding.crawl.chapter.customers.detail2'),
  _t('onboarding.crawl.chapter.customers.detail3'),
];
const WAITING_MESSAGE_INTERVAL_MS = 2400;

function waitingStartedAt(progressKey?: string): number {
  const now = Date.now();
  if (!progressKey) return now;
  const storageKey = `onboarding.customersWaitingStartedAt.${progressKey}`;
  try {
    const saved = Number(sessionStorage.getItem(storageKey));
    if (Number.isFinite(saved) && saved > 0) return saved;
    sessionStorage.setItem(storageKey, String(now));
  } catch {
    // Storage is an enhancement only; the animation still works without it.
  }
  return now;
}

function shownWaitingMessageCount(startedAt: number): number {
  return Math.min(WAITING_MESSAGES.length, Math.floor((Date.now() - startedAt) / WAITING_MESSAGE_INTERVAL_MS) + 1);
}

function ProgressLines({ progressKey, liveMessages }: { progressKey?: string; liveMessages: string[] }) {
  const [openedAt] = useState(() => Math.max(0, liveMessages.length - 1));
  const live = foldProgressLines(liveMessages.slice(openedAt));
  if (live.length > 0) {
    return (
      <div className="flex flex-col gap-2" data-testid="onboarding-scan-customers-waiting" role="status" aria-live="polite">
        {live.slice(-4).map((row, i, arr) => (
          <div
            key={row.text}
            className={cn(
              'transition-all duration-500',
              i < arr.length - 1 ? 'text-muted-foreground opacity-40' : 'text-foreground font-medium',
            )}
            style={{ fontSize: '1.1rem', lineHeight: 1.5, letterSpacing: '-0.005em' }}
          >
            {row.text}
            {row.elapsed && <span className="ml-2 tabular-nums text-muted-foreground">{row.elapsed}</span>}
          </div>
        ))}
      </div>
    );
  }
  return <CannedWaitingMessages progressKey={progressKey} />;
}

function CannedWaitingMessages({ progressKey }: { progressKey?: string }) {
  const [startedAt] = useState(() => waitingStartedAt(progressKey));
  const [shown, setShown] = useState(() => shownWaitingMessageCount(startedAt));

  useEffect(() => {
    if (shown >= WAITING_MESSAGES.length) return;
    const nextRevealAt = startedAt + shown * WAITING_MESSAGE_INTERVAL_MS;
    const timer = window.setTimeout(() => {
      setShown(prev => Math.max(Math.min(prev + 1, WAITING_MESSAGES.length), shownWaitingMessageCount(startedAt)));
    }, Math.max(0, nextRevealAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [shown, startedAt]);

  return (
    <div className="flex flex-col gap-2">
      {WAITING_MESSAGES.slice(0, shown).map((msg, i, arr) => (
        <div
          key={msg}
          className={cn(
            'transition-all duration-500',
            i < arr.length - 1 ? 'text-muted-foreground opacity-40' : 'text-foreground font-medium',
          )}
          style={{ fontSize: '1.1rem', lineHeight: 1.5, letterSpacing: '-0.005em' }}
        >
          {msg}
        </div>
      ))}
    </div>
  );
}

interface Props {
  progressKey?: string;
  liveMessages?: string[];
  facts?: AudienceFact[];
}

export default function CustomersWaiting({ progressKey, liveMessages = [], facts = [] }: Props) {
  return (
    <div className="flex flex-col gap-6">
      {facts.length > 0 && <AudienceFacts facts={facts} />}
      <ProgressLines progressKey={progressKey} liveMessages={liveMessages} />
    </div>
  );
}
